/**
 * GuidesPage — index for the CaraLoom Knowledge Center (iter 174).
 *
 * B2B counterpart to the (archived) /resources surface. Renders every
 * guide in data/guides.js with proper metadata, cross-links, breadcrumb
 * schema, and Article-list-style cards.
 *
 * Route: /guides
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, BookOpen, Clock } from 'lucide-react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { Seo } from '../components/seo/Seo';
import {
  organizationSchema, websiteSchema, breadcrumbSchema,
} from '../lib/seoSchemas';
import { GUIDES } from '../data/guides';

const HEADING_SCHEMA = [
  organizationSchema(),
  websiteSchema(),
  breadcrumbSchema([
    { name: 'Home', path: '/' },
    { name: 'Guides', path: '/guides' },
  ]),
];

function GuideCard({ g }) {
  return (
    <Link
      to={`/guides/${g.slug}`}
      className="cn-card cn-card-hover rounded-3xl block group p-6"
      data-testid={`guide-card-${g.slug}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <span className="cn-eyebrow !mb-0 text-cn-accent">{g.category}</span>
        <span className="text-xs text-cn-text-muted inline-flex items-center gap-1">
          <Clock className="w-3 h-3" /> {g.readMinutes} min read
        </span>
      </div>
      <h3 className="cn-display text-xl md:text-2xl text-cn-text leading-tight group-hover:text-cn-primary transition-colors">
        {g.title}
      </h3>
      <p className="text-cn-text-muted leading-relaxed text-sm md:text-base mt-3">{g.excerpt}</p>
      <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-cn-primary">
        Read the guide <ArrowRight className="w-3.5 h-3.5" />
      </span>
    </Link>
  );
}

export default function GuidesPage() {
  return (
    <div className="min-h-screen bg-cn-bg" data-testid="guides-page">
      <Seo
        title="CaraLoom Guides — Discharge Planning & Post-Acute Care Knowledge Center"
        description="The CaraLoom Knowledge Center — authoritative guides on discharge planning, SNF vs IRF, levels of post-acute care, home health and hospice eligibility, and CMS-compliant patient choice."
        path="/guides"
        keywords={[
          'discharge planning guide',
          'post acute care guide',
          'SNF vs IRF',
          'home health eligibility',
          'hospice eligibility',
          'levels of post acute care',
          'hospital discharge checklist',
        ]}
        schema={HEADING_SCHEMA}
      />
      <PublicNavbar />

      <section className="relative overflow-hidden pt-16 pb-14 md:pt-24 md:pb-16">
        <div className="cn-container">
          <nav aria-label="Breadcrumb" className="text-xs text-cn-text-muted mb-6" data-testid="guides-breadcrumb">
            <Link to="/" className="hover:text-cn-primary">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-cn-text/70">Guides</span>
          </nav>
          <div className="inline-flex items-center gap-1.5 rounded-full bg-cn-primary/10 border border-cn-primary/25 px-3 py-1 text-xs font-semibold text-cn-primary tracking-wide uppercase mb-5">
            <BookOpen className="h-3 w-3" /> Knowledge Center
          </div>
          <h1 className="cn-display text-4xl sm:text-5xl lg:text-6xl text-cn-text tracking-tight leading-[1.05] max-w-3xl">
            Discharge planning &amp; post-acute care, explained.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-cn-text-muted leading-relaxed max-w-2xl">
            Authoritative guides for hospital case managers, discharge planners, and post-acute providers. Written and reviewed by CaraLoom&rsquo;s clinical team. Updated when the underlying regulations change.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/discharge-planning-software"
              className="inline-flex items-center gap-1.5 bg-cn-primary text-white rounded-full px-6 py-3 font-semibold text-sm hover:bg-cn-primary/90 transition-colors"
              data-testid="guides-primary-cta"
            >
              See the platform <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/for-discharge-planners"
              className="inline-flex items-center gap-1.5 border border-cn-ink/20 text-cn-ink rounded-full px-6 py-3 font-semibold text-sm hover:border-cn-primary/60 transition-colors"
              data-testid="guides-secondary-cta"
            >
              How planners use CaraLoom <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="cn-container pb-24" data-testid="guides-list">
        <div className="grid md:grid-cols-2 gap-5">
          {GUIDES.map((g) => (
            <GuideCard key={g.slug} g={g} />
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
